// src/components/InputArea.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  styled,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import HistoryIcon from '@mui/icons-material/History';

const InputContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderRadius: 16,
  backgroundColor: theme.palette.surface?.main || theme.palette.background.paper,
  border: '1px solid rgba(148, 163, 184, 0.2)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  '&:hover, &:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 8px 24px rgba(0, 210, 255, 0.2)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    lineHeight: '1.5',
    padding: theme.spacing(1.5, 0),
    color: theme.palette.text.primary,
    fontSize: '1rem',
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.8,
    }
  },
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

// Recent queries component
const RecentQueries = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '100%',
  left: 0,
  right: 0,
  backgroundColor: theme.palette.surface?.main || theme.palette.background.paper,
  border: '1px solid rgba(148, 163, 184, 0.1)',
  borderRadius: 12,
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
  maxHeight: 200,
  overflowY: 'auto',
  zIndex: 10,
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(148, 163, 184, 0.3)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  }
}));

const QueryItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  marginBottom: theme.spacing(0.5),
}));

export default function InputArea({
  onSendMessage,
  isGenerating,
  placeholder = 'Ask about any stock or market trend…'
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [value, setValue] = useState('');
  const [currentRows, setCurrentRows] = useState(1);
  const [showRecent, setShowRecent] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  // Get recent queries from localStorage
  const [recentQueries, setRecentQueries] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recentQueries')) || [];
    } catch {
      return [];
    }
  });

  // Save recent query
  const saveQuery = (query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    
    const newQueries = [
      trimmedQuery,
      ...recentQueries.filter(q => q !== trimmedQuery)
    ].slice(0, 5); // Keep only last 5 queries
    
    setRecentQueries(newQueries);
    localStorage.setItem('recentQueries', JSON.stringify(newQueries));
  };

  // auto-grow up to 5 rows
  useEffect(() => {
    if (value === '') {
        setCurrentRows(1);
        return;
    }
    const lineCount = value.split('\n').length;
    const approximateLineLength = Math.ceil(value.length / 50); // Estimate lines from characters
    setCurrentRows(Math.min(Math.max(lineCount, approximateLineLength, 1), 5));
  }, [value]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current && !isMobile) {
      inputRef.current.focus();
    }
  }, [isMobile]);

  // Close recent queries when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowRecent(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const send = () => {
    const text = value.trim();
    if (!text || isGenerating) return;
    
    saveQuery(text);
    onSendMessage(text);
    setValue('');
  };

  const handleKeyDown = e => {
    // Handle keyboard shortcuts
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    } else if (e.key === 'Escape') {
      setShowRecent(false);
    } else if (e.key === 'ArrowUp' && value === '') {
      // Show recent queries when pressing up arrow in empty input
      e.preventDefault();
      setShowRecent(true);
    }
  };

  const handleSelectRecent = (query) => {
    setValue(query);
    setShowRecent(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <Box 
      sx={{ 
        width: '100%', 
        maxWidth: 700, 
        mb: { xs: 1, sm: 2 },
        position: 'relative',
      }}
      ref={containerRef}
    >
      {showRecent && recentQueries.length > 0 && (
        <RecentQueries>
          {recentQueries.map((query, index) => (
            <QueryItem key={index} onClick={() => handleSelectRecent(query)}>
              {query}
            </QueryItem>
          ))}
        </RecentQueries>
      )}
      
      <InputContainer elevation={2}>
        <Tooltip title="Show recent queries">
          <IconButton
            onClick={() => setShowRecent(!showRecent)}
            size="small"
            color="inherit"
            sx={{ mr: 1, opacity: 0.7 }}
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <StyledTextField
          fullWidth
          multiline
          inputRef={inputRef}
          rows={currentRows}
          placeholder={placeholder}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={() => setShowRecent(false)}
          variant="standard"
          InputProps={{ 
            disableUnderline: true,
          }}
          disabled={isGenerating}
          sx={{ py: 0.5 }}
        />
        
        {isGenerating ? (
          <CircularProgress size={24} thickness={4} sx={{ mx: 1 }} />
        ) : (
          <>
            {!isMobile && (
              <Tooltip title="Voice input (coming soon)">
                <IconButton
                  color="inherit"
                  sx={{ opacity: 0.5 }}
                  disabled={true}
                >
                  <MicIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              color="primary"
              onClick={send}
              disabled={!value.trim() || isGenerating}
              sx={{
                ml: 0.5,
                transition: 'all 0.2s ease',
                '&.Mui-disabled': {
                  opacity: 0.4,
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </>
        )}
      </InputContainer>
      
      {!isMobile && (
        <Box sx={{ 
          mt: 1, 
          textAlign: 'center', 
          fontSize: '0.75rem', 
          color: 'text.secondary', 
          opacity: 0.7 
        }}>
          Press Enter to send, Shift+Enter for new line
        </Box>
      )}
    </Box>
  );
}